/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.ContentWidgetService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods for retrieving data sources
 */
ContentWidgetService.$inject = [
	'$q',
	'$window',
	'mx.sb.Ownership.OwnershipService',
	'mx.SolutionBuilderAgent.Http',
	'mx.shell.EventService',
	'mx.shell.ObjectMapper',
	'mx.shell.Config',
	'mx.internationalization',
	'mx.shell.NotificationService',
	'mx.shell.AppsService',
	'mx.shell.NavigationItemsLoaderService',
	'mx.SolutionBuilderAgent.DataModelService',
	'mx.SolutionBuilderAgent.designerOpener',
];

export default function ContentWidgetService(
	$q,
	$window,
	ownershipService,
	http,
	eventService,
	objectMapper,
	mxConfig,
	i18n,
	notificationService,
	appsService,
	navigationItemsLoaderService,
	dataModelService,
	designerOpener
) {
	let syncWidgetId;
	const cache = { data: {} };

	window.addEventListener(
		'storage',
		function (event) {
			if (event.key === 'WidgetToRefresh' && event.newValue === syncWidgetId) {
				eventService.dispatchEvent('mx.SolutionBuilderAgent.SynchronizeWidget');
			}
		},
		false
	);

	return {
		getAllAppViewsUncached: getAllAppViewsUncached,
		getViews: getViews,
		getNewDialogData: getNewDialogData,
		getNewFragment: getNewFragment,
		getDialogData: getDialogData,
		updateDialogData: updateDialogData,
		get: get,
		update: update,
		getHtml: getHtml,
		publishLayout: publishLayout,
		widgetSync: widgetSync,
		setViewUrl: setViewUrl,
		extendCorrespondingUrl: extendCorrespondingUrl,
		getCorrespondingUrl: getCorrespondingUrl,
		revert: revertTemplate,
		getAllViews: getAllViews,
		getWidgetInfoById: getWidgetInfoById,
		getDefaultViewType: getDefaultViewType,
		setDefaultViewType: setDefaultViewType,
		openConfigurePreview: openConfigurePreview,
		customizeViewInDesigner: customizeViewInDesigner,
		isCustomizable: isCustomizable,
		getWidgetIdForControl: getWidgetIdForControl,
		getWidgetIdFromView: getWidgetIdFromView,
		checkViewUnavailableReasons: checkViewUnavailableReasons,
		getWidgetType: getWidgetType,
	};

	function getWidgetIdFromView(view) {
		if (!view) {
			return null;
		}
		const widgetType = getWidgetType(view);
		switch (widgetType) {
			case mx.SolutionBuilderAgent.ContentWidgetType.Wizard:
			case mx.SolutionBuilderAgent.ContentWidgetType.Dialog:
			case mx.SolutionBuilderAgent.ContentWidgetType.ObjectDetails:
				return view.locals && view.locals.widgetId;
			case mx.SolutionBuilderAgent.ContentWidgetType.LandingPage:
				return view.widgetId;
			case mx.SolutionBuilderAgent.ContentWidgetType.List:
				return view.id;
		}
	}

	function getConfigForView(view) {
		let widgetType = getWidgetType(view);
		const openDialogIfNoPreview = mxConfig.get('openDialogIfNoPreview');
		const key = 'customizationPromise_' + widgetType + (openDialogIfNoPreview && view.type ? '_' + view.type : '');

		if (cache.data[key]) {
			return cache.data[key];
		}

		cache.data[key] = $q.all([navigationItemsLoaderService.load(), openDialogIfNoPreview ? _countPreviews(view.type) : $q.when(undefined)]).then(function (args) {
			const navigationItems = args[0];
			const previewsAmount = args[1];

			if (!Array.isArray(navigationItems)) {
				return $q.reject('navigation items is not loaded');
			}

			const config = {
				applicationId: 'f7e35986-46e1-e511-dd9b-74d02b9d869c', // Administration
			};

			if (openDialogIfNoPreview && widgetType === mx.SolutionBuilderAgent.ContentWidgetType.ObjectDetails && previewsAmount === 0) {
				widgetType = mx.SolutionBuilderAgent.ContentWidgetType.Dialog;
			}

			switch (widgetType) {
				case mx.SolutionBuilderAgent.ContentWidgetType.Wizard:
					config.navigationId = '171eb056-4d68-e511-7882-bc5ff41a70c0';
					config.entityType = 'PDRContentWidgetTypeWizard';
					break;
				case mx.SolutionBuilderAgent.ContentWidgetType.ObjectDetails:
					config.navigationId = '7004e6f8-98be-e511-82ba-00248cfe5192';
					config.entityType = 'PDRContentWidgetTypeObjectDetails';
					break;
				case mx.SolutionBuilderAgent.ContentWidgetType.Dialog:
					config.navigationId = 'f637a7f8-6830-e511-14a9-00248cfe5192';
					config.entityType = 'PDRContentWidgetTypeDialog';
					break;
				case mx.SolutionBuilderAgent.ContentWidgetType.LandingPage:
					config.navigationId = 'b63da3ae-9479-e611-db9b-847beb134555';
					config.entityType = 'PDRContentWidgetTypeLandingPage';
					break;
				case mx.SolutionBuilderAgent.ContentWidgetType.List:
					config.navigationId = '476304e2-6830-e511-14a9-00248cfe5192';
					config.entityType = 'PDRContentWidgetTypeList';
					break;
				case mx.SolutionBuilderAgent.ContentWidgetType.EmailTemplate:
					break;
			}

			const apps = appsService.getApplications();
			if (
				!apps.some(function (application) {
					if (application.value === config.applicationId) {
						config.applicationName = application.name;
						return true;
					}
				})
			) {
				return $q.reject(mx.warnCatchMessage('User has no permissions for necessary application (or application is not found)'));
			}

			return getViewsByType(config.entityType).then(function (views) {
				if (Array.isArray(views) && views.length > 0) {
					const configViews = views
						.filter(function (viewOfType) {
							return viewOfType.WidgetType === mx.SolutionBuilderAgent.ContentWidgetViewType.ObjectDetails && viewOfType.Enabled !== false;
						})
						.sort(function (view1, view2) {
							return (view2.Priority || 0) - (view1.Priority || 0);
						});

					if (configViews.length === 0) {
						return $q.reject('User has no permissions for necessary view (or view is not found)');
					}
					config.dialogId = configViews[0].Id;

					return config;
				}
			});
		});
		return cache.data[key];
	}

	function _countPreviews(entityType) {
		if (!entityType) {
			return $q.when(undefined);
		}
		return getViews(entityType).then(function (views) {
			return (views || []).filter(function (view) {
				return view.WidgetType === mx.SolutionBuilderAgent.ContentWidgetType.ObjectDetails;
			}).length;
		});
	}

	function getWidgetType(view) {
		if (view.isWizard) {
			if (view.locals && view.locals.wizard) {
				if (view.locals.wizard.wizardMode !== mx.SolutionBuilderAgent.WizardMode.Default) {
					return mx.SolutionBuilderAgent.ContentWidgetType.Dialog;
				}
			}

			return mx.SolutionBuilderAgent.ContentWidgetType.Wizard;
		} else if (view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Preview) {
			return mx.SolutionBuilderAgent.ContentWidgetType.ObjectDetails;
		} else if (view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit || view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.New) {
			return mx.SolutionBuilderAgent.ContentWidgetType.Dialog;
		} else if (view.isLandingPage) {
			return mx.SolutionBuilderAgent.ContentWidgetType.LandingPage;
		} else if (view.isSearchPage) {
			return mx.SolutionBuilderAgent.ContentWidgetType.List;
		}
	}

	function isCustomizable(view) {
		return getConfigForView(view).then(
			function () {
				return true;
			},
			function () {
				return false;
			}
		);
	}

	async function customizeViewInDesigner(view) {
		const widgetId = getWidgetIdFromView(view);
		const config = await getConfigForView(view);
		designerOpener.open(config.entityType, widgetId);
	}

	function openConfigurePreview(view) {
		let configureUrl;
		const basePath = mxConfig.settings.basePath;

		return getConfigForView(view).then(
			function (config) {
				const widgetId = getWidgetIdFromView(view);
				if (!widgetId) {
					return $q.reject('WidgetID for view is not found');
				}
				configureUrl =
					basePath +
					'app-' +
					config.applicationName +
					'/search-page/' +
					config.navigationId +
					'/nofilter/' +
					'?view-options=' +
					JSON.stringify({
						objectId: widgetId,
						dialogId: config.dialogId,
						navigationId: config.navigationId,
						archived: '0',
						type: config.entityType,
						viewType: 'preview',
					});

				$window.open(configureUrl, '_blank');
			},
			function (error) {
				notificationService.error(i18n.get('solutionBuilderAgent.common.no-configure-URL-for-view'));
				mx.console.error(error);
			}
		);
	}

	function extendCorrespondingUrl(view, extraParams) {
		if (view.correspondingUrl) {
			try {
				let obj = JSON.parse(view.correspondingUrl.replace('view-options=', ''));
				if (typeof obj === 'object') {
					obj = angular.extend(obj, extraParams);
					view.correspondingUrl = 'view-options=' + JSON.stringify(obj);
				}
			} catch (err) {
				console.error('Error while trying to extend corresponding url: ' + err);
			}
		}
	}

	function getCorrespondingUrl(view, dialogId, objectContext, fullScreen) {
		view = view || {};
		objectContext = objectContext || {};
		const isArchived = objectContext._archived ? '1' : '0';
		const objectId = objectContext._id;
		const type = objectContext._type;

		const viewOptions = {
			objectId: objectId,
			dialogId: dialogId,
			archived: isArchived,
			type: type,
			viewType: 'preview',
			fullScreen: fullScreen,
		};

		switch (view.dialogType) {
			case mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit:
				viewOptions.viewType = 'edit';
				break;
			case mx.SolutionBuilderAgent.ContentWidgetDialogAction.New:
				viewOptions.viewType = 'new';
				delete viewOptions.archived;
				delete viewOptions.objectId;
				break;
		}

		if (view.isWizard && !view.ShowAsWizard) {
			angular.extend(viewOptions, {
				actionId: view.actionId,
				viewType: 'action',
			});
			delete viewOptions.archived;
			delete viewOptions.dialogId;
		}

		return 'view-options=' + JSON.stringify(viewOptions);
	}

	function getPrintUrl(view, dialogId, objectContext) {
		if (view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Preview) {
			objectContext = objectContext || {};
			const isArchived = objectContext._archived ? '1' : '0';
			const objectId = objectContext._id;
			const type = objectContext._type;

			const basePath = mxConfig.settings.basePath;
			const urlParams = type + '/' + objectId + '/' + isArchived + '/' + (dialogId || '');
			return basePath + 'print-details/1/' + urlParams;
		}
	}

	function setViewUrl(view, dialogId, objectContext) {
		view.correspondingUrl = getCorrespondingUrl(view, dialogId, objectContext);
		view.uiActionGroup.push('with-corresponding-url');
		view.printUrl = getPrintUrl(view, dialogId, objectContext);
	}

	function widgetSync(id) {
		syncWidgetId = id;
	}

	function checkViewUnavailableReasons(objectTypeName, dialogType) {
		return appsService.getCurrentApplication().then(function (currentApp) {
			return checkViewsByAppAndType(currentApp.value, objectTypeName, dialogType).then(function (errorMessage) {
				return errorMessage;
			});
		});
	}

	function getViewsByType(objectTypeName, forAllApps) {
		return getAllViews(false, forAllApps).then(function (views) {
			const filteredViews = views.filter(function (view) {
				return view.Entity === objectTypeName;
			});
			if (filteredViews.length === 0) {
				return $q.reject(mx.warnCatchMessage('User has no permissions for necessary application (or application is not found)'));
			}
			return filteredViews;
		});
	}

	function getViewsByAppAndType(appCode, objectTypeName) {
		return http.get('api/WidgetCommon/GetWidgets/' + objectTypeName + '/' + appCode);
	}

	function checkViewsByAppAndType(appCode, objectTypeName, dialogType) {
		return http.get('api/WidgetCommon/CheckWidgets/' + objectTypeName + '/' + appCode + '/' + dialogType, {}, {}, true).then(function () {
			return $q.reject('Dialog is not valid');
		});
	}

	function getAllAppViewsUncached(typeName) {
		const appCode = 'all';

		if (typeName) {
			return getViewsByAppAndType(appCode, typeName);
		} else {
			return getViewsByAppAndType(appCode);
		}
	}

	function getViews(objectTypeName, forAllApps) {
		const key = 'getViews_' + objectTypeName + (forAllApps ? '_all' : '');

		if (!cache.data[key]) {
			cache.data[key] = getViewsByType(objectTypeName, forAllApps);
		}
		return cache.data[key];
	}

	function getWidgetInfoById(id) {
		const key = 'getAllViews_dictionary';
		let widgetPromise = cache.data[key];

		if (!widgetPromise) {
			const defer = $q.defer();
			widgetPromise = cache.data[key] = defer.promise;
			getAllViews(false).then(function (views) {
				const widgetDictionary = {};
				views.forEach(function (widget) {
					widgetDictionary[widget.Id] = widget;
				});
				defer.resolve(widgetDictionary);
			});
		}

		return widgetPromise.then(function (widgetDictionary) {
			const widgetInfo = widgetDictionary[id];
			if (widgetInfo) {
				return widgetInfo;
			}

			return http.get('api/WidgetCommon/' + id + '/html').then(function (widgetBase) {
				const widgetInfo = {
					Priority: widgetBase.Priority,
					EntityIcon: widgetBase.EntityIcon,
					EntityColor: widgetBase.EntityColor,
					AddActionPosition: widgetBase.AddActionPosition,
					VisibilityCondition: widgetBase.VisibilityCondition,
					Entity: widgetBase.Entity,
					EntityDisplayName: widgetBase.EntityDisplayName,
					Id: widgetBase.Id,
					Name: widgetBase.Name,
				};
				widgetDictionary[id] = widgetInfo;
				return widgetInfo;
			});
		});
	}

	function getAllViews(forceReload, forAllApps) {
		const key = 'getAllViews' + (forAllApps ? '_allApps' : '');
		let widgetInfos = cache.data[key];

		if (widgetInfos && !forceReload) {
			return widgetInfos;
		}

		widgetInfos = getCurrentAppCode(forAllApps).then(function (appCode) {
			return http.get('api/WidgetCommon/GetWidgets/' + appCode).then();
		});

		cache.data[key] = widgetInfos;
		return widgetInfos;
	}

	function getCurrentAppCode(forAllApps) {
		const allKey = 'all';
		return forAllApps ? $q.when(allKey) : appsService.getCurrentApplication().then((app) => (app && (app.value || app.value === 0) ? app.value : allKey));
	}

	function getNewDialogData(widgetId, params, selectedObjects, context) {
		let parameters = '';

		if (params) {
			parameters = params + '&selectedObjects=';
			if (Array.isArray(selectedObjects)) {
				for (let i = 0; i < selectedObjects.length; i++) {
					if (i > 0) {
						parameters += ',';
					}
					parameters += selectedObjects[i]._id;
				}
			}
		}
		parameters = "'" + JSON.stringify(parameters) + "'";

		return getAppHeaders(context).then(function (headers) {
			headers.headers['Content-Type'] = 'text/plain;charset=utf-8';
			return http.post(`api/WidgetDialog/GetNewData/${widgetId}`, parameters, headers, {}, true).then(function (item) {
				if (!item) {
					return $q.reject('no permission for data');
				}
				// TODO: refactor this
				return {
					displayStrings: item.DisplayStrings,
					entity: objectMapper.mapItem(item.Entity),
				};
			});
		});
	}

	function getNewFragment(fragmentName) {
		return http.get(`api/WidgetDialog/GetNewFragment/${fragmentName}`);
	}

	async function getDialogData(widgetId, objectId, archivedOnly, loadLocalization, context) {
		const dataUri = `api/WidgetCommon/GetData/${widgetId}/${objectId}?archived=${!!archivedOnly}${loadLocalization ? '&loadLocalization=true' : ''}`;

		const headers = await getAppHeaders(context);
		const item = await http.get(dataUri, undefined, headers, true);

		return {
			displayStrings: item.DisplayStrings,
			entity: objectMapper.mapItem(item.Entity),
		};
	}

	function updateDialogData(widgetId, object, context) {
		const result = dataModelService.cleanFromEmptyOptionalDataDefinition(object);
		const data = `'${JSON.stringify(result)}'`;

		return getAppHeaders(context).then((headers) => http.post(`api/WidgetDialog/UpdateData/${widgetId}`, data, headers, {}, true));
	}

	function getAppHeaders(context) {
		return getCurrentAppCode().then((app) => ({ headers: $.extend(true, { 'mx-application-id': app }, context) }));
	}

	function get(id) {
		const key = `widget_${id}`;
		let widget = cache.data[key];

		if (!widget) {
			widget = http.get(`api/widgetCommon/${id}`);
			cache.data[key] = widget;
		}

		return widget;
	}

	function getDefaultViewType(widgetId) {
		const res = localStorage.getItem(`mx.WidgetDefaultViewType.${widgetId}`);

		return res !== null && !isNaN(res) ? Number(res) : res;
	}

	function setDefaultViewType(widgetId, defaultView) {
		return localStorage.setItem('mx.WidgetDefaultViewType.' + widgetId, defaultView);
	}

	function publishLayout(template) {
		cache.data['widget_' + template.WidgetId] = null;
		return postTemplate(template, 'Publish');
	}

	function update(template) {
		cache.data['widget_' + template.WidgetId] = null;
		return postTemplate(template, 'Update');
	}

	async function getHtml(id, isCreateDialogWidget) {
		const htmlUrl = `api/widgetCommon/${id}/html`;
		const widget = await http.get(htmlUrl, {}, {}, true);
		await ownershipService.injectOwnershipContainer(widget, isCreateDialogWidget);
		return widget;
	}

	function postTemplate(template, methodName, languageHeader) {
		if (!template || typeof template !== 'object') {
			throw new Error(i18n.get('solutionBuilderAgent.errors.content_widget_service'));
		}

		return http.post(`api/widgetTemplate/${methodName}`, template, languageHeader).then(function (data) {
			localStorage.removeItem('WidgetToRefresh');
			localStorage.setItem('WidgetToRefresh', template.id);
			return data;
		});
	}

	function revertTemplate(widgetId, revertToSystem) {
		if (!widgetId || widgetId === '00000000-0000-0000-0000-000000000000') {
			throw new Error(i18n.get('solutionBuilderAgent.services.contentWidget.noWidgetId'));
		}

		const url = revertToSystem ? 'api/WidgetTemplate/RevertToSystem/' : 'api/WidgetTemplate/Revert/';

		return http.get(url + widgetId).then(function (data) {
			localStorage.removeItem('WidgetToRefresh');
			localStorage.setItem('WidgetToRefresh', widgetId);
			return data;
		});
	}

	function getWidgetIdForControl(controller, scope) {
		if (!scope) {
			if (controller) {
				// scope is passed as only argument
				if ('$parent' in controller) {
					scope = controller;
				}

				controller = 'vm' in scope ? scope.vm : null;
			} else {
				return null;
			}
		}

		const parentView = controller?.parentViewController?.mxView;
		let widgetId;

		if (parentView && (widgetId = getWidgetIdFromView(parentView))) {
			return widgetId;
		}

		// Fallback logic using scopes
		let currentScope = scope;

		while (currentScope && !widgetId) {
			widgetId = currentScope.vm ? getWidgetIdFromDataModel(currentScope.vm.dataModel) : null;
			currentScope = currentScope.$parent;
		}

		return widgetId;

		function getWidgetIdFromDataModel(dataModel) {
			return (dataModel && dataModel.Context && dataModel.Context.WidgetId) || null;
		}
	}
}
